import React, { useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { useOrderData } from '../../../hooks/useOrderData';
import { useLineItems } from '../../../hooks/useLineItems';
import { useBulkAssignment } from '../../../hooks/useBulkAssignment';
import { CompleteFulfillmentOptions } from './CompleteFulfillmentOptions';
import { PartialFulfillmentOptions } from './PartialFulfillmentOptions';
import { useOrderSelection } from '../../../hooks/useOrderSelection';
import { useLineItemSelection } from '../../../hooks/useLineItemSelection';

interface BulkAssignmentDialogProps {
  visible: boolean;
  onHide: () => void;
  orderData: ReturnType<typeof useOrderData>;
  lineItemsHook: ReturnType<typeof useLineItems>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemSelection: ReturnType<typeof useLineItemSelection>;
}

export const BulkAssignmentDialog: React.FC<BulkAssignmentDialogProps> = ({
  visible,
  onHide,
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemSelection
}) => {
  const toast = useRef<Toast>(null);
  const order = orderSelection.selectedOrder;
  const { refetch } = lineItemsHook;
  const { selectedLineItems, clearSelection } = lineItemSelection;
  const {
    analysisPhase,
    selectedStoreCode,
    selectedPartialOption,
    selectedFulfillmentType,
    completeOptions,
    partialOptions,
    handleStoreSelect,
    handlePartialSelect,
    handleFulfillmentTypeChange,
    handleExecuteAssignment
  } = useBulkAssignment({
    visible,
    order,
    selectedLineItems,
    refetch,
    clearSelection,
    refreshSingleOrder: orderData.refreshSingleOrder
  });

  const getTotalQuantity = () => {
    return selectedLineItems.reduce((sum, item) => sum + item.product_quantity, 0);
  };

  const handleExecuteWithToast = async () => {
    const result = await handleExecuteAssignment();
    
    if (result.success) {
      toast.current?.show({
        severity: 'success',
        summary: 'Bulk Assignment Successful',
        detail: result.message,
        life: 3000,
      });
      onHide();
    } else {
      toast.current?.show({
        severity: 'error',
        summary: 'Assignment Failed',
        detail: result.error,
        life: 5000,
      });
    }
  };

  const renderAnalysisPhase = () => (
    <div className="text-center p-6">
      <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      <h5 className="mt-4 mb-2">Analyzing Inventory</h5>
      <p className="text-color-secondary">
        Checking inventory across all stores for complete and partial fulfillment options...
      </p>
    </div>
  );

  const renderExecutionPhase = () => (
    <div className="text-center p-6">
      <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      <h5 className="mt-4 mb-2">Executing Assignment</h5>
      <p className="text-color-secondary">
        Assigning items to selected location(s)...
      </p>
    </div>
  );

  const renderFulfillmentOptions = () => (
    <TabView 
      activeIndex={selectedFulfillmentType === 'complete' ? 0 : 1}
      onTabChange={(e) => handleFulfillmentTypeChange(e.index === 0 ? 'complete' : 'partial')}
    >
      <TabPanel 
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-check-circle" style={{ marginRight: '5px' }}></i>
            <span>Complete Fulfillment</span>
            {completeOptions.length > 0 && (
              <Badge value={completeOptions.length} severity="success" style={{ marginLeft: '8px' }} />
            )}
          </div>
        }
      >
        <CompleteFulfillmentOptions
          completeOptions={completeOptions}
          selectedStoreCode={selectedStoreCode}
          selectedLineItems={selectedLineItems}
          onStoreSelect={handleStoreSelect}
        />
      </TabPanel>
      
      <TabPanel 
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-sitemap" style={{ marginRight: '5px' }}></i>
            <span>Partial Fulfillment</span>
            {partialOptions.length > 0 && (
              <Badge value={partialOptions.length} severity="info" style={{ marginLeft: '8px' }} />
            )}
          </div>
        }
      >
        <PartialFulfillmentOptions
          partialOptions={partialOptions}
          selectedPartialOption={selectedPartialOption}
          onPartialSelect={handlePartialSelect}
        />
      </TabPanel>
    </TabView>
  );

  const renderFooter = () => {
    if (analysisPhase === 'analyzing' || analysisPhase === 'executing') {
      return (
        <div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={onHide}
            disabled={analysisPhase === 'executing'}
          />
        </div>
      );
    }

    const hasSelection = selectedStoreCode || selectedPartialOption !== null;
    const buttonLabel = selectedStoreCode 
      ? "Assign to Store" 
      : selectedPartialOption !== null 
        ? "Assign to Stores"
        : "Select an Option";

    return (
      <div>
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-text"
          onClick={onHide}
        />
        <Button
          label={buttonLabel}
          icon="pi pi-check"
          className="p-button-primary"
          onClick={handleExecuteWithToast}
          disabled={!hasSelection}
          autoFocus
        />
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        style={{ width: '800px' }}
        header="Bulk Assignment - Fulfillment Options"
        modal
        footer={renderFooter()}
        onHide={onHide}
      >
        <div>
          <div className="mb-4">
            <h6>Order: {order?.order_name}</h6>
            <div className="flex gap-4 mb-3">
              <div className="flex align-items-center gap-2 py-2">
                <Badge value={selectedLineItems.length} severity="info" />
                <span className="font-bold px-2">Selected Items</span>
              </div>
              <div className="flex align-items-center gap-2 py-2">
                <Badge value={getTotalQuantity()} severity="info" />
                <span className="font-bold px-2">Total Quantity</span>
              </div>
            </div>
          </div>

          {analysisPhase === 'analyzing' && renderAnalysisPhase()}
          {analysisPhase === 'executing' && renderExecutionPhase()}
          {analysisPhase === 'presenting' && renderFulfillmentOptions()}
        </div>
      </Dialog>
    </>
  );
}; 